/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * GraphQL Request Wrapper
 * ---------------------------------
 * WHY:
 * - Standardizes GraphQL queries & mutations
 * - Automatically detects query vs mutation
 * - Centralizes error handling
 * - Returns clean response data
 */

import { gqlHandleError } from '@/services/graphqlEndPointCommonSetting/graphqlErrorHandler'
import { getGraphqlClient } from '@/services/graphqlEndPointCommonSetting/gqlClient'

const excludedOperations = [
    'generateCustomerToken',
    'createCustomerV2',
    'resetPassword',
    'requestPasswordResetEmail',
]

/**
 * Executes GraphQL query or mutation safely
 */
export async function wrapGqlRequest<T = any>(
    queryOrMutation: string,
    variables: Record<string, any> = {},
    operationName = ''
): Promise<T> {
    const passAuthHeader = excludedOperations.includes(operationName)

    const client = getGraphqlClient({ passAuthHeader: !passAuthHeader })

    try {
        // Detect mutation vs query
        const isMutation = /^\s*mutation\b/i.test(queryOrMutation)

        // Execute GraphQL operation
        const operation = isMutation
            ? client.mutation(queryOrMutation, variables)
            : client.query(queryOrMutation, variables)

        const response = await operation.toPromise()

        // Handle GraphQL or network errors
        if (response.error) {
            await gqlHandleError(response.error as any, variables, client.url, operationName)
        }

        // Return named operation result or full response data
        if (operationName && response.data) {
            return (response.data as any)[operationName]
        }

        return response.data as T
    } catch (error: any) {
        await gqlHandleError(error, variables, client.url, operationName)
        throw error
    }
}
